/**
 * Hook that powers the branded "Add to Home Screen" banner.
 *
 * Handles three platforms in one surface:
 *
 *   - **Android / Chromium** — captures `beforeinstallprompt`, stashes it,
 *     and exposes `promptInstall()` which fires the native install dialog.
 *   - **iOS Safari** — never fires `beforeinstallprompt`; instead we detect
 *     the platform and show a "Tap Share → Add to Home Screen" guide
 *     (the InstallBanner component renders this variant).
 *   - **Already installed** — when the app is running in standalone display
 *     mode (`display-mode: standalone` or iOS `navigator.standalone`), the
 *     banner is suppressed entirely.
 *
 * Dismissal is persisted per-tenant via `scopedStorageKey` so dismissing on
 * one restaurant doesn't suppress the banner on another.
 */

import { useCallback, useEffect, useState } from "react";

import { scopedStorageKey } from "@/lib/storageScope";

const DISMISSED_STORAGE_KEY = "v2_pwa_install_dismissed";

/** Window-typed shim — `BeforeInstallPromptEvent` isn't in lib.dom yet. */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: ReadonlyArray<string>;
  prompt(): Promise<void>;
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
}

type Platform = "android" | "ios" | "desktop" | "unsupported";

function detectPlatform(): Platform {
  if (typeof navigator === "undefined") return "unsupported";
  const ua = navigator.userAgent || "";
  // iPadOS 13+ reports as Mac; disambiguate via touch.
  const isIPad =
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/.test(ua) || isIPad) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (
    window.matchMedia &&
    window.matchMedia("(display-mode: standalone)").matches
  ) {
    return true;
  }
  // iOS Safari sets this on the navigator when launched from home screen.
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

export type InstallPromptState = {
  /** Banner should be shown right now. */
  canShow: boolean;
  /** Platform — drives the banner's copy (native button vs iOS guide). */
  platform: Platform;
  /** True when the native Android/Chromium prompt is available. */
  hasNativePrompt: boolean;
  /** Fire the native install dialog (Android/Chromium only). No-op elsewhere. */
  promptInstall: () => Promise<"accepted" | "dismissed" | "unavailable">;
  /** User dismissed — persist and hide. */
  dismiss: () => void;
};

export function useInstallPrompt(slug: string | null): InstallPromptState {
  const [platform] = useState<Platform>(() => detectPlatform());
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    null,
  );
  const [installed, setInstalled] = useState<boolean>(() => isStandalone());
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return (
        localStorage.getItem(scopedStorageKey(DISMISSED_STORAGE_KEY, slug)) ===
        "1"
      );
    } catch {
      return false;
    }
  });

  // Re-read dismissal when the tenant slug changes.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      setDismissed(
        localStorage.getItem(scopedStorageKey(DISMISSED_STORAGE_KEY, slug)) ===
          "1",
      );
    } catch {
      setDismissed(false);
    }
  }, [slug]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onBeforeInstall = (e: Event) => {
      // Prevent Chrome's mini-infobar so we can show our branded banner instead.
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return "unavailable" as const;
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      // The event can only be used once — drop the reference either way.
      setDeferred(null);
      if (choice.outcome === "accepted") {
        setInstalled(true);
      }
      return choice.outcome;
    } catch {
      setDeferred(null);
      return "unavailable" as const;
    }
  }, [deferred]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(scopedStorageKey(DISMISSED_STORAGE_KEY, slug), "1");
    } catch {
      /* storage unavailable — in-memory dismissal still applies */
    }
  }, [slug]);

  // Show rules:
  //   - Hide when already installed (standalone mode).
  //   - Hide when the user dismissed it for this tenant.
  //   - On Android/Chromium: only show once we've captured `beforeinstallprompt`
  //     (no point showing a button that fires nothing).
  //   - On iOS: show the guide regardless — there's no event to wait for.
  //   - On desktop: only show if we got the prompt (rare but supported).
  const hasNativePrompt = deferred !== null;
  let canShow = !installed && !dismissed;
  if (canShow) {
    if (platform === "ios") {
      // Already true — iOS doesn't gate on an event.
    } else {
      canShow = hasNativePrompt;
    }
  }

  return { canShow, platform, hasNativePrompt, promptInstall, dismiss };
}
